import React from 'react';
import KeyboardReact from 'react-simple-keyboard';
import type { KeyboardReactInterface } from 'react-simple-keyboard';
import { useIntl } from 'react-intl';

import {
  BUTTON_LABELS,
  OPTIONS_BY_LANG,
  getLayoutsByLanguage,
  getWritableInputTypesConfig, DEFAULT_KEY_LABELS,
} from '../../utils/KeyUtils';

import type { KeyboardComponentProps, KeyboardOptionsExt } from 'components/Keyboard/types';
import { KeyboardLayoutType } from 'components/Keyboard/types';
import { DEFAULT_THEME, SPECIAL_KEYS } from 'components/Keyboard/utils/constants';
import { MODULES } from 'components/Keyboard/modules/utils';
import type Language from 'types/LanguageEnum';

export const DEFAULT_BUTTON_THEME = [
  {
    class: '!bg-gray-300',
    buttons: [
      SPECIAL_KEYS.BACKSPACE,
      SPECIAL_KEYS.SHIFT,
      SPECIAL_KEYS.CONFIRM,
      SPECIAL_KEYS.ENTER,
      SPECIAL_KEYS.SPECIAL,
      SPECIAL_KEYS.DEFAULT,
    ].join(' '),
  },
  {
    class: '!bg-gray-300 !w-28 !flex-grow-0',
    buttons: `${SPECIAL_KEYS.NEXT_KEYBOARD}`,
  },
  {
    class: '!max-w-[100px] !min-w-[50px]',
    buttons: [SPECIAL_KEYS.AT, SPECIAL_KEYS.DEFAULT, SPECIAL_KEYS.SPECIAL].join(' '),
  },
  {
    class: '!w-[20px]',
    buttons: `${SPECIAL_KEYS.BACKSPACE}`,
  },
];

const getInitialKeyboardName = (language: Language, layoutType: KeyboardLayoutType) => {
  const layouts = getLayoutsByLanguage(language);
  const index = layoutType === KeyboardLayoutType.ALTERNATE ? layouts.length - 1 : 0;
  return layouts.at(index)?.name;
};

const DefaultKeyboard: React.FC<KeyboardComponentProps> = ({
  type,
  language,
  layoutType,
  layoutCandidates,
  onInit,
  onChange,
  keyboardRef,
  onKeyReleased,
  onSelectionChange,
}) => {
  const intl = useIntl();
  const keyboardInstance = React.useRef<KeyboardReactInterface | null>(null);
  const [keyboardName, setKeyboardName] = React.useState(() => getInitialKeyboardName(language, layoutType));

  const display = React.useMemo(() => {
    const labels = BUTTON_LABELS(intl);
    const defaultKeyLabel = DEFAULT_KEY_LABELS[keyboardName];
    if (!defaultKeyLabel) {
      return labels;
    }
    return { ...labels, [SPECIAL_KEYS.DEFAULT]: defaultKeyLabel };
  }, [intl, keyboardName]);

  const handleInit = React.useCallback((keyboard: KeyboardReactInterface) => {
    keyboardInstance.current = keyboard;
    onInit(keyboard);
  }, [onInit]);

  const handleKeyReleased = React.useCallback((key: string) => {
    const options = keyboardInstance.current?.getOptions() as KeyboardOptionsExt | undefined;
    const currentName = options?.keyboardName;
    if (currentName && currentName !== keyboardName) {
      setKeyboardName(currentName);
    }
    onKeyReleased(key);
  }, [keyboardName, onKeyReleased]);

  const excludedKeys = React.useMemo(
    () => getWritableInputTypesConfig(language)[type]?.excludedKeys,
    [type, language],
  );

  const layoutCandidatesPageSize = React.useMemo(
    () => OPTIONS_BY_LANG[language].layoutCandidatesPageSize,
    [language],
  );

  const modules = React.useMemo(() => MODULES[language], [language]);

  return (
    <KeyboardReact
      type={type}
      mergeDisplay
      newLineOnEnter
      onInit={handleInit}
      display={display}
      modules={modules}
      onChange={onChange}
      language={language}
      layoutType={layoutType}
      keyboardRef={keyboardRef}
      buttonTheme={DEFAULT_BUTTON_THEME}
      onKeyReleased={handleKeyReleased}
      excludeFromLayout={excludedKeys}
      layoutCandidates={layoutCandidates}
      layoutCandidatesCaseSensitiveMatch
      onSelectionChange={onSelectionChange}
      theme={`${DEFAULT_THEME} fb-keyboard`}
      layoutCandidatesPageSize={layoutCandidatesPageSize}
    />
  );
};

export default DefaultKeyboard;
